import { AuthActionTypes } from '@app/auth/actions/auth';
import { GridActionTypes } from '@app/core/actions/grid';
import { modalReducer, State as ModalState } from '@app/core/pcs-modal';
import * as fromAlert from '@app/core/reducers/alert';
import * as fromConfig from '@app/core/reducers/config';
import * as fromLoader from '@app/core/reducers/loader';
import * as fromPreviousRouter from '@app/core/reducers/previous-router';
import * as fromTranslations from '@app/core/reducers/translations';
import { environment } from '@env/environment';
import * as fromRouter from '@ngrx/router-store';
import { ROUTER_NAVIGATION } from '@ngrx/router-store';
import { ActionReducer, ActionReducerMap, createFeatureSelector, createSelector, MetaReducer } from '@ngrx/store';
import { hasValue, reducerWithResetAction } from '@util';
import { RouterStateUrl } from '@util/routing';
import { gridReducer, NgRxGridState } from 'ngrx-data-grid';
import * as R from 'ramda';

export interface State {
  router: fromRouter.RouterReducerState<RouterStateUrl>;
  previousRouter: fromPreviousRouter.State;
  translations: fromTranslations.State;
  alert: fromAlert.State;
  loader: fromLoader.State;
  config: fromConfig.State;
  modal: ModalState;
  grid: NgRxGridState;
}

export function translationsReducer(state, action) {
  return fromTranslations.reducer(state, action);
}

export function alertReducer(state, action) {
  return fromAlert.reducer(state, action);
}

export function loaderReducer(state, action) {
  return fromLoader.reducer(state, action);
}

export function configReducer(state, action) {
  return fromConfig.reducer(state, action);
}

export function previousRouterReducer(state, action) {
  return fromPreviousRouter.reducer(state, action);
}

export function pcsGridReducer(state, action) {
  return reducerWithResetAction(GridActionTypes.ResetState, gridReducer)(state, action);
}

export const reducers: ActionReducerMap<State> = {
  router: fromRouter.routerReducer,
  previousRouter: previousRouterReducer,
  translations: translationsReducer,
  alert: alertReducer,
  loader: loaderReducer,
  config: configReducer,
  modal: modalReducer,
  grid: pcsGridReducer
};

/**
 * console.log all actions.
 *
 * @param {ActionReducer} reducer a reducer
 * */
export function logger(reducer: ActionReducer<State>): ActionReducer<State> {
  return (state: State, action: any): State => {
    console.log('state', state);
    console.log('action', action);

    return reducer(state, action);
  };
}

type RetainConfigAndRouter = (state: State) => Partial<State>;
export const retainConfigAndRouter: RetainConfigAndRouter = R.pick(['config', 'router']);

/**
 * Reset state on log out.
 *
 * @param reducer a reducer
 */
export function clearState(reducer: ActionReducer<State>): ActionReducer<State> {
  /**
   * @param state current state
   * @param action current action
   * @return state consisting of only app config and router if action is log out, current state otherwise
   */
  return (state: State, action: any): State => reducer(action.type === AuthActionTypes.Logout ? retainConfigAndRouter(state) as State : state, action);
}

export function copyRouterState(reducer: ActionReducer<State>): ActionReducer<State> {
  return (state: State, action: any): State => {
    const routerState: RouterStateUrl = R.path(['router', 'state'], state) as RouterStateUrl;
    return reducer(action.type === ROUTER_NAVIGATION && hasValue(routerState)
      ? R.mergeRight(state, {
        previousRouter: {
          state: routerState
        }
      }) as State : state, action);
  };
}

/**
 * An array of meta-reducers that will be composed to form the root meta-reducer.
 * */
export const metaReducers: MetaReducer<State>[] = !environment.production
  ? [clearState, logger, copyRouterState]
  : [clearState, copyRouterState];

export const getRouterState = createFeatureSelector<fromRouter.RouterReducerState>('router');

export const getState = createSelector(
  getRouterState,
  (state: any) => state && state.state
);

export const getStateParams = createSelector(
  getState,
  (state: any) => state && state.params
);

export const getQueryParams = createSelector(
  getState,
  (state: any) => state && state.queryParams
);

export const getRouteData = createSelector(
  getState,
  (state: any) => state && state.data
);

export const getRouteTitle = createSelector(
  getRouteData,
  R.prop('title')
);

export const selectTranslations = createFeatureSelector<fromTranslations.State>('translations');

export const getCurrentLanguage = createSelector(
  selectTranslations,
  fromTranslations.getCurrentLanguage
);

export const selectAlert = createFeatureSelector<fromAlert.State>('alert');

export const getAlerts = createSelector(
  selectAlert,
  fromAlert.getAlerts
);

export const selectLoader = createFeatureSelector<fromLoader.State>('loader');

export const getIsLoading = createSelector(
  selectLoader,
  fromLoader.getIsLoading
);

export const selectConfig = createFeatureSelector<fromConfig.State>('config');

export const getAppConfig = createSelector(
  selectConfig,
  fromConfig.getAppConfig
);

export const getAppInfo = createSelector(
  selectConfig,
  fromConfig.getAppInfo
);

export const getPcsDocumentationLink = createSelector(
  getAppInfo,
  fromConfig.getDocumentation
);

export const getMailPcsSupport = createSelector(
  getAppInfo,
  fromConfig.getMailPcsSupport
);

export const getAppDateFormat = createSelector(
  getAppConfig,
  (appConfig) => hasValue(appConfig) ? appConfig.dateFormat : null
);

export const getModalState = createFeatureSelector<ModalState>(
  'modal'
);

export const getModals = createSelector(
  getModalState,
  state => state.modals
);

export const getGridState = createFeatureSelector<NgRxGridState>('grid');

export const getPreviousRouterState = createFeatureSelector<fromPreviousRouter.State>('previousRouter');

export const getPreviousRouter = createSelector(
  getPreviousRouterState,
  fromPreviousRouter.getRouterState
);

export const getPreviousStateParams = createSelector(
  getPreviousRouter,
  fromPreviousRouter.getStateParams
);
